<script setup lang="ts">
import { onBeforeUnmount, watch } from "vue";
import { useRouter } from "vue-router";

import SidebarMenu from "@/components/navigation/SidebarMenu.vue";
import { useMobileSidebarMenu } from "@/composables/useMobileSidebarMenu";

const { isMenuOpen, closeMenu } = useMobileSidebarMenu();

const router = useRouter();
router.beforeEach(() => {
  closeMenu();
});

watch(
  isMenuOpen,
  (open) => {
    document.documentElement.classList.toggle("overflow-hidden", open);
  },
  { flush: "sync" },
);

onBeforeUnmount(() => {
  document.documentElement.classList.remove("overflow-hidden");
});
</script>

<template>
    <!-- Single root so PrivateLayout flex row stays [sidebar column][main], not one flex item per fragment node -->
    <div class="sidebar-shell relative z-50 shrink-0 w-0 min-w-0 lg:w-64 lg:min-h-0 flex flex-col">
        <!-- DESKTOP SIDEBAR -->
        <aside
            class="hidden lg:flex lg:flex-col lg:flex-1 lg:min-h-0 lg:overflow-y-auto lg:overflow-x-hidden rounded-lg shadow-lg bg-base-100 mt-4 w-full max-w-full"
        >
            <SidebarMenu />
        </aside>

        <!-- MOBILE SIDEBAR -->
        <div class="lg:hidden" role="dialog" aria-modal="true">
            <transition
                enter-active-class="transition-opacity ease-linear"
                enter-from-class="opacity-0"
                enter-to-class="opacity-100"
                leave-active-class="transition-opacity ease-linear"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
            >
                <div
                    v-if="isMenuOpen"
                    class="fixed inset-0 bg-zinc-900/80 z-60"
                    aria-hidden="true"
                    @click="closeMenu()"
                />
            </transition>
            <transition
                enter-active-class="transition-opacity ease-linear"
                enter-from-class="opacity-0"
                enter-to-class="opacity-100"
                leave-active-class="transition-opacity ease-linear"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
            >
                <div v-if="isMenuOpen" class="fixed inset-0 z-60 flex pointer-events-none">
                    <transition
                        enter-active-class="transition ease-in-out transform"
                        enter-from-class="-translate-x-full"
                        enter-to-class="translate-x-0"
                        leave-active-class="transition ease-in-out transform"
                        leave-from-class="translate-x-0"
                        leave-to-class="-translate-x-full"
                    >
                        <div
                            v-if="isMenuOpen"
                            class="pointer-events-auto relative flex h-dvh max-h-dvh min-h-0 w-[min(22rem,92vw)] max-w-88 flex-none shadow-2xl"
                        >
                            <aside
                                class="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden overflow-y-auto overscroll-contain bg-base-100 pt-[env(safe-area-inset-top,0px)] pb-[env(safe-area-inset-bottom,0px)] pl-[max(0.5rem,env(safe-area-inset-left,0px))] [-webkit-overflow-scrolling:touch]"
                            >
                                <SidebarMenu />
                            </aside>
                            <div
                                class="flex w-12 shrink-0 flex-col border-l border-white/15 bg-zinc-900/90 supports-backdrop-filter:backdrop-blur-sm"
                            >
                                <button
                                    type="button"
                                    class="flex min-h-14 flex-1 items-center justify-center touch-manipulation text-white active:bg-white/15"
                                    @click="closeMenu()"
                                >
                                    <span class="sr-only">Close menu</span>
                                    <i class="pi pi-times text-xl" />
                                </button>
                            </div>
                        </div>
                    </transition>
                </div>
            </transition>
        </div>
    </div>
</template>

<style scoped>
.sidebar-shell aside {
    scrollbar-width: thin;
}
</style>